/* eslint-disable no-unused-expressions */
import React, { useState, useRef, useEffect } from "react";

import { slugify } from "transliteration";
import { v4 as uuidv4 } from "uuid";

// import JoditEditor from "jodit-react";
// import 'jodit/build/jodit.js'
import JoditReactEditor from "../../../utils/Editor/JoditReactWrapper";

import "./promopostEditor.css";

const imageExtensions = [
  ".jpg",
  ".JPG",
  ".png",
  ".PNG",
  ".jpeg",
  ".JPEG",
  ".gif",
  ".GIF"
];

const checkIfImage = (nameString) => {
  let isImage = false;

  imageExtensions.forEach((extension) => {
    if (nameString.includes(extension)) {
      isImage = true;
      return;
    }
  });

  return isImage;
};

const PromopostEditor = ({
  promopost,
  isFromUpdate,
  isFromCopy,
  handleSavePromopost,
  handleCancel,
  staticMenus,
  mediaURLs,
  setMediaURLs
}) => {
  const originalPromopost = { ...promopost };
  const [promopostInEdit, setPromopostInEdit] = useState({ ...promopost });

  const joditRef = useRef(null);
  const [bodyContent, setBodyContent] = useState(promopost.promopostBody);

  const [isHTMLCodeVisible, setHTMLCodeVisible] = useState(false);

  const [isFileInputEmpty, setIsFileInputEmpty] = useState(true);
  const [isDropzoneHighlighted, setDropzoneHighlighted] = useState(false);
  const fileInput = useRef(null);
  const copiedRef = useRef(null);

  const [filePreviewStrings, setFilePreviewStrings] = useState([]);
  const [documentPreviewStrings, setDocumentPreviewStrings] = useState([]);

  // Non-files handlers
  const handleNameChange = (e) => {
    let workingObject = { ...promopostInEdit };

    workingObject.promopostName = e.target.value;
    workingObject.sectionURL = slugify(workingObject.promopostName, {
      lowercase: true,
      separator: "_"
    });

    setPromopostInEdit({ ...workingObject });
  };

  const handleHeadingChange = (e) => {
    let workingObject = { ...promopostInEdit };

    workingObject.promopostHeading = e.target.value;

    setPromopostInEdit({ ...workingObject });
  };

  const handlePromopostDateChange = (e) => {
    let workingObject = { ...promopostInEdit };

    workingObject.promopostDate = e.target.value;

    setPromopostInEdit({ ...workingObject });
  };

  const handleChangeMenuReference = (e) => {
    let workingObject = { ...promopostInEdit };

    workingObject.menuReference = e.target.value;

    setPromopostInEdit({ ...workingObject });
  };

  const handleBodyEditorStateChange = (newHTML) => {
    let workingObject = { ...promopostInEdit };

    workingObject.promopostBody = newHTML;

    setPromopostInEdit({ ...workingObject });
  };

  // Thumbnail
  const handleChangeGalleryPictureURL = (url) => {
    let workingObject = { ...promopostInEdit };

    workingObject.galleryPictureURL = url;

    setPromopostInEdit({ ...workingObject });
  };

  const handleDeleteGalleryPictureURL = () => {
    let workingObject = { ...promopostInEdit };

    workingObject.galleryPictureURL = "";

    setPromopostInEdit({ ...workingObject });
  };

  // Files input
  const handleUploadFiles = (e) => {
    if (e) {
      e.preventDefault();
    }

    try {
      Array.from(fileInput.current.files).forEach((file) => {
        let reader = new FileReader();
        reader.onloadend = function () {
          let newFile = {
            name: file.name,
            url: reader.result,
            type: checkIfImage(file.name) ? "picture" : "file",
            _id: uuidv4()
          };

          setMediaURLs((mediaURLs) => [...mediaURLs, newFile]);
        };

        if (file) {
          reader.readAsDataURL(file);
        }
      });

      fileInput.current.value = "";
      setIsFileInputEmpty(true);
      setFilePreviewStrings((filePreviewStrings) => []);
      setDocumentPreviewStrings((documentPreviewStrings) => []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCopyFileURL = (fileURL) => {
    try {
      navigator.clipboard.writeText(`${fileURL}`);

      copiedRef.current.style.animation = "";
      copiedRef.current.offsetWidth;

      copiedRef.current.style.animation = "copiedFaeInOut 1s forwards";
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteFile = (pictureURL) => {
    try {
      setMediaURLs((mediaURLs) =>
        mediaURLs.filter((file) => file.url !== pictureURL)
      );
    } catch (err) {
      console.log(err);
    }
  };

  const handleFileInputChange = (e) => {
    setFilePreviewStrings((filePreviewStrings) => []);
    setDocumentPreviewStrings((documentPreviewStrings) => []);
    const { files } = e.target;

    if (files.length > 0) {
      setIsFileInputEmpty(false);

      Array.from(files).forEach((file) => {
        if (checkIfImage(file.name)) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener("load", () => {
            setFilePreviewStrings((filePreviewStrings) => [
              ...filePreviewStrings,
              reader.result
            ]);
          });
        } else {
          setDocumentPreviewStrings((documentPreviewStrings) => [
            ...documentPreviewStrings,
            file.name
          ]);
        }
      });
    } else if (files.length === 0) {
      setIsFileInputEmpty(true);
      setFilePreviewStrings((filePreviewStrings) => []);
      setDocumentPreviewStrings((documentPreviewStrings) => []);
    }
  };

  // Dropzone handlers
  const handleOpenFileDialog = () => {
    fileInput.current.click();
  };

  const handleOnDragOver = (e) => {
    e.preventDefault();

    setDropzoneHighlighted(true);
  };

  const handleOnDragLeave = () => {
    setDropzoneHighlighted(false);
  };

  const handleOnDrop = (e) => {
    e.preventDefault();

    const files = e.dataTransfer.files;

    fileInput.current.files = files;

    if (files.length > 0) {
      setFilePreviewStrings((filePreviewStrings) => []);
      setDocumentPreviewStrings((documentPreviewStrings) => []);
      setIsFileInputEmpty(false);

      Array.from(files).forEach((file) => {
        if (checkIfImage(file.name)) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener("load", () => {
            setFilePreviewStrings((filePreviewStrings) => [
              ...filePreviewStrings,
              reader.result
            ]);
          });
        } else {
          setDocumentPreviewStrings((documentPreviewStrings) => [
            ...documentPreviewStrings,
            file.name
          ]);
        }
      });
    } else if (files.length === 0) {
      setIsFileInputEmpty(true);
      setFilePreviewStrings((filePreviewStrings) => []);
      setDocumentPreviewStrings((documentPreviewStrings) => []);
    }

    setDropzoneHighlighted(false);
  };

  const joditConfig = {
    readonly: false,
    language: "en",
    imageDefaultWidth: 200
  };

  // Update the HTML state of the promopostInEdit with every change of the Editor content
  useEffect(() => {
    handleBodyEditorStateChange(bodyContent);
  }, [bodyContent]);

  return (
    <div className="promopost">
      <div className="promopost__containerDiv">
        <div className="containerDiv__promopostMainInfo">
          <div>
            <h4>POST NAME</h4>
            <input
              type="text"
              name="promopostName"
              id="promopostName"
              onChange={handleNameChange}
              value={promopostInEdit.promopostName}
            />
          </div>
          <div>
            <h4>POST HEADING</h4>
            <textarea
              style={{ fontFamily: "Arial" }}
              cols="33"
              rows="2"
              name="promopostHeading"
              id="promopostHeading"
              onChange={handleHeadingChange}
              value={promopostInEdit.promopostHeading}
            />
          </div>
          <div>
            <h4>URL</h4>
            {promopostInEdit.sectionURL}
          </div>
          <div>
            <h4>DATE</h4>
            <input
              type="date"
              name="promopostDate"
              id="promopostDate"
              value={promopostInEdit.promopostDate}
              onChange={handlePromopostDateChange}
            />
          </div>
        </div>
        <h4>BODY</h4>
        <div className="containerDiv__promopostBodyEditor">
          <JoditReactEditor
            ref={joditRef}
            value={bodyContent}
            config={joditConfig}
            tabIndex={1}
            onChange={(newContent) => setBodyContent(newContent)}
          />
          <button
            className="promopostBodyEditor__showHTMlBtn"
            onClick={() => setHTMLCodeVisible(isHTMLCodeVisible ? false : true)}
          >
            {isHTMLCodeVisible ? "Hide HTML" : "Show HTML"}
          </button>
          <textarea
            style={{
              width: "100%",
              display: `${isHTMLCodeVisible ? "block" : "none"}`
            }}
            value={promopostInEdit.promopostBody}
            cols="30"
            rows="10"
            disabled
          />
        </div>
        <div className="containerDiv__promopostFiles">
          <h3>Media files</h3>
          <div ref={copiedRef} className={`file__copied`}>
            Copied!
          </div>
          <div className="promopostFiles__filesContainer">
            {mediaURLs.length && mediaURLs.length > 0 ? (
              mediaURLs.map((file, index) =>
                file.type === "picture" ? (
                  <div
                    key={file._id}
                    className="filesContainer__uploadedPictureDiv"
                  >
                    <img
                      src={file.url}
                      alt={file.name}
                      title="click or tap to copy URL"
                      onClick={() => handleCopyFileURL(file.url)}
                    />
                    <button
                      title="delete file"
                      className="uploadedPictureDiv__deleteBtn"
                      onClick={() => handleDeleteFile(file.url)}
                    >
                      <svg
                        className="bi bi-x-circle-fill"
                        width="1.5em"
                        height="1.5em"
                        viewBox="0 0 20 20"
                        fill="var(--btn-body-color-red)"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM7.354 6.646L10 9.293l2.646-2.647a.5.5 0 01.708.708L10.707 10l2.647 2.646a.5.5 0 01-.708.708L10 10.707l-2.646 2.647a.5.5 0 01-.708-.708L9.293 10 6.646 7.354a.5.5 0 11.708-.708z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      title="mark as thumbnail"
                      className="uploadedPictureDiv__markHPGallery"
                      onClick={() => handleChangeGalleryPictureURL(file.url)}
                    >
                      <svg
                        className="bi bi-star-fill"
                        width="1.5em"
                        height="1.5em"
                        viewBox="0 0 20 20"
                        fill="var(--btn-body-color-yellow)"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M5.612 17.443c-.386.198-.824-.149-.746-.592l.83-4.73-3.522-3.356c-.33-.314-.16-.888.282-.95l4.898-.696 2.184-4.327c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.283.95l-3.523 3.356.83 4.73c.078.443-.36.79-.746.592L10 15.187l-4.389 2.256z" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div
                    key={file._id}
                    className="filesContainer__uploadedDocumentDiv"
                  >
                    <div onClick={() => handleCopyFileURL(file.url)}>
                      <span>{file.name}</span>
                    </div>
                    <button onClick={() => handleDeleteFile(file.urlL)}>
                      <svg
                        className="bi bi-x-circle-fill"
                        width="1.5em"
                        height="1.5em"
                        viewBox="0 0 20 20"
                        fill="red"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM7.354 6.646L10 9.293l2.646-2.647a.5.5 0 01.708.708L10.707 10l2.647 2.646a.5.5 0 01-.708.708L10 10.707l-2.646 2.647a.5.5 0 01-.708-.708L9.293 10 6.646 7.354a.5.5 0 11.708-.708z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )
              )
            ) : (
              <div className="filesContainer__emptyMessageDiv">
                No uploaded files
              </div>
            )}
          </div>
          <h3>attach files</h3>
          <div className="promopostFiles__uploadFilesForm">
            <div
              className={`uploadFilesForm__dropzone
                                            ${
                                              isDropzoneHighlighted
                                                ? "uploadFilesForm__dropzone--highlighted"
                                                : ""
                                            }
                                        `}
              onClick={handleOpenFileDialog}
              onDragOver={handleOnDragOver}
              onDragLeave={handleOnDragLeave}
              onDrop={handleOnDrop}
            >
              <p>
                click or drag & drop file
                <br />
                <svg
                  className="bi bi-image"
                  width="1.5em"
                  height="1.5em"
                  viewBox="0 0 20 20"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.002 4h-12a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1V5a1 1 0 00-1-1zm-12-1a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2h-12z"
                    clipRule="evenodd"
                  />
                  <path d="M12.648 9.646a.5.5 0 01.577-.093l3.777 1.947V16h-14v-2l2.646-2.354a.5.5 0 01.63-.062l2.66 1.773 3.71-3.71z" />
                  <path
                    fillRule="evenodd"
                    d="M6.502 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
                    clipRule="evenodd"
                  />
                </svg>
              </p>
            </div>
            <form onSubmit={(e) => handleUploadFiles(e)}>
              <label
                className="uploadFilesForm__chooseFilesBtn"
                htmlFor="multiple_upload"
              >
                <input
                  ref={fileInput}
                  type="file"
                  name="multiple_upload"
                  id="multiple_upload"
                  multiple
                  onChange={(e) => handleFileInputChange(e)}
                />
                choose files
                <br />
                <svg
                  className="bi bi-folder-fill"
                  width="1.5em"
                  height="1.5em"
                  viewBox="0 0 20 20"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.828 5h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0115.174 16H4.826a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31L2.5 5a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 0011.828 5zm-8.322.12C3.72 5.042 3.95 5 4.19 5h5.396l-.707-.707A1 1 0 008.172 4H4.5a1 1 0 00-1 .981l.006.139z"
                    clipRule="evenodd"
                  />
                </svg>
              </label>
              <div className="uploadFilesForm__previewsDiv">
                {filePreviewStrings.length > 0 ||
                documentPreviewStrings.length > 0 ? (
                  <>
                    <h3>Selected files</h3>
                    {filePreviewStrings.length > 0 ? (
                      <p>Pictures selected: {filePreviewStrings.length}</p>
                    ) : null}
                    {documentPreviewStrings.length > 0 ? (
                      <p>Files selected: {documentPreviewStrings.length}</p>
                    ) : null}
                    <div className="previewsDiv__imageContainer">
                      {filePreviewStrings.length > 0 &&
                        filePreviewStrings.map((base64String, index) => {
                          return (
                            <div
                              className="previewsDiv__imagePreview"
                              key={index}
                            >
                              <img src={base64String} alt="" />
                            </div>
                          );
                        })}
                    </div>
                    <div className="previewsDiv__documentContainer">
                      {documentPreviewStrings.length > 0 &&
                        documentPreviewStrings.map((documentName, index) => {
                          return (
                            <div
                              className="previewsDiv__documentPreview"
                              key={index}
                            >
                              <div>
                                <span>{documentName}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </>
                ) : null}
              </div>
              <button
                className="uploadFilesForm__uploadBtn"
                type="submit"
                disabled={isFileInputEmpty ? true : false}
              >
                upload files
                <br />
                <svg
                  className="bi bi-upload"
                  width="1.5em"
                  height="1.5em"
                  viewBox="0 0 20 20"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M2.5 10a.5.5 0 01.5.5V14a1 1 0 001 1h12a1 1 0 001-1v-3.5a.5.5 0 011 0V14a2 2 0 01-2 2H4a2 2 0 01-2-2v-3.5a.5.5 0 01.5-.5zM7 6.854a.5.5 0 00.707 0L10 4.56l2.293 2.293A.5.5 0 1013 6.146L10.354 3.5a.5.5 0 00-.708 0L7 6.146a.5.5 0 000 .708z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M10 4a.5.5 0 01.5.5v8a.5.5 0 01-1 0v-8A.5.5 0 0110 4z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </form>
          </div>
        </div>
        <div className="containerDiv__promopostMenuReference">
          <h4>MENU REFERENCE</h4>
          {promopostInEdit.menuReference
            ? staticMenus.find(
                (menu) => menu._id === promopostInEdit.menuReference
              ).name
            : "No menus selected"}
          <div style={{ marginBottom: ".5em", marginTop: ".5em" }} />
          {staticMenus && staticMenus.length ? (
            <select
              name="promopostMenuReference"
              id="promopostMenuReference"
              onChange={handleChangeMenuReference}
              value={promopostInEdit.menuReference}
            >
              <option value="">Select menu</option>
              {staticMenus.map((menu) => (
                <option key={menu._id} value={menu._id}>
                  {menu.name}
                </option>
              ))}
            </select>
          ) : (
            "No available static menus"
          )}
        </div>
        <div className="containerDiv__promopostGalleryPicture">
          <h4>Thumbnail picture</h4>
          {promopostInEdit.galleryPictureURL ? (
            <div className="promopostGalleryPicture__containerDiv">
              <button
                title="remove thumbnail"
                className="promopostGalleryPicture__deleteBtn"
                onClick={() => handleDeleteGalleryPictureURL()}
              >
                <svg
                  className="bi bi-x-circle-fill"
                  width="1.5em"
                  height="1.5em"
                  viewBox="0 0 20 20"
                  fill="var(--btn-body-color-red)"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7.354 6.646L10 9.293l2.646-2.647a.5.5 0 01.708.708L10.707 10l2.647 2.646a.5.5 0 01-.708.708L10 10.707l-2.646 2.647a.5.5 0 01-.708-.708L9.293 10 6.646 7.354a.5.5 0 11.708-.708z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <img
                className="promopostGalleryPicture__img"
                src={promopostInEdit.galleryPictureURL}
                alt="thumbnail"
              />
            </div>
          ) : (
            "Thumbnail not selected"
          )}
        </div>
      </div>
      <div className="promopost__controlsDiv">
        <button
          className="controlsDiv__editPostBtn"
          onClick={
            isFromUpdate
              ? () => {
                  handleSavePromopost(promopost._id, promopostInEdit);
                  handleCancel();
                }
              : isFromCopy
              ? () => {
                  handleSavePromopost(promopostInEdit);
                  handleCancel();
                }
              : () => {
                  handleSavePromopost(promopostInEdit);
                  setPromopostInEdit({ ...promopost });
                }
          }
        >
          SAVE
        </button>
        <button
          className="controlsDiv__cancelPostBtn"
          onClick={
            !isFromCopy && !isFromUpdate
              ? () => {
                  setPromopostInEdit({ ...promopost });
                }
              : () => {
                  let werePicturesUpdated = false;
                  if (
                    originalPromopost.picturesURLs &&
                    promopostInEdit.picturesURLs
                  ) {
                    let originalArray = originalPromopost.picturesURLs.map(
                      (item) => item.pictureURL
                    );
                    let newArray = promopostInEdit.picturesURLs.map(
                      (item) => item.pictureURL
                    );

                    if (originalArray.length !== newArray.length) {
                      werePicturesUpdated = true;
                    } else if (originalArray.length === newArray.length) {
                      for (let i = 0; i < originalArray.length; i++) {
                        if (originalArray[i] !== newArray[i]) {
                          werePicturesUpdated = true;
                          break;
                        }
                      }
                    }
                  }

                  if (werePicturesUpdated) {
                    let updatedPicturesURLs = promopostInEdit.picturesURLs;
                    let workingObject = { ...promopost };
                    workingObject.picturesURLs = [...updatedPicturesURLs];

                    handleSavePromopost(promopost._id, workingObject);

                    handleCancel();
                  } else {
                    handleCancel();
                  }
                }
          }
        >
          CANCEL
        </button>
      </div>
    </div>
  );
};

export default PromopostEditor;
